import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import SearchRequests from "../classes/SearchRequests";
import D3EDisposable from "../rocket/D3EDisposable";
import Popup from "./Popup";
import PageNavigator from "../classes/PageNavigator";
import Timer from "../utils/Timer";
import ToolTipInfoMessage from "./ToolTipInfoMessage";
import UserType from "../classes/UserType";
import RequestStatus from "../classes/RequestStatus";
import PaginationView from "./PaginationView";
import Query from "../classes/Query";
import NOKUser from "../models/NOKUser";
import MessageDispatch from "../rocket/MessageDispatch";
import SearchComponent from "./SearchComponent";
import Button from "./Button";
import StaffAddOrEditRequestView from "./StaffAddOrEditRequestView";
import SearchRequestsRequest from "../models/SearchRequestsRequest";
import HospitalUtils from "../classes/HospitalUtils";
import Notification from "../models/Notification";
import Administrator from "../models/Administrator";
import Duration from "../core/Duration";
import TextView from "./TextView";
import SearchRequest from "../models/SearchRequest";
import DateUtils from "../classes/DateUtils";
import HospitalStatus from "../classes/HospitalStatus";
import { UsageConstants } from "../rocket/D3ETemplate";
import { StyleThemeData } from "./ThemeWrapper";
import { BuildContext } from "../classes/BuildContext";

type _AddButtonOnPressed = (d3eState: StaffRequestTabRefs) => void;

export interface StaffRequestTabProps extends BaseUIProps {
  key?: string;
  user: NOKUser;
  admin?: Administrator;
  notification?: Notification;
}
/// To store state data for StaffRequestTab
class StaffRequestTabRefs {
  public addButton: AddButtonState = new AddButtonState();
}

interface AddButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: StaffRequestTabRefs;
  _onAddButtonClicked?: _AddButtonOnPressed;
}

class AddButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _AddButtonWithState extends ObservableComponent<AddButtonWithStateProps> {
  addButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get addButton(): AddButtonState {
    return this.props.d3eState.addButton;
  }
  public get d3eState(): StaffRequestTabRefs {
    return this.props.d3eState;
  }
  public get _onAddButtonClicked(): _AddButtonOnPressed {
    return this.props._onAddButtonClicked;
  }
  public initState() {
    super.initState();

    this.updateObservable("addButton", null, this.addButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["addButton", "addButton.", "addButton.hover"], this.rebuild);
  }
  public addButtonOnEnter(event): void {
    return this.addButton.setHover(true);
  }
  public addButtonOnExit(event): void {
    return this.addButton.setHover(false);
  }
  public dispose(): void {
    this.addButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      width: 150,
      child: Button({
        padding: this.addButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.addButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.addButton.disable,
        onPressed: () => {
          this._onAddButtonClicked(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "+ Make Request" }),
        onEnter: (event) => {
          this.addButtonOnEnter(event);
        },
        onExit: (event) => {
          this.addButtonOnExit(event);
        },
      }),
      className: "xfbf hc",
    });
  }
}
function AddButtonWithState(props: AddButtonWithStateProps) {
  return React.createElement(_AddButtonWithState, props);
}

class _StaffRequestTabState extends ObservableComponent<StaffRequestTabProps> {
  static defaultProps = { user: null, admin: null, notification: null };
  d3eState: StaffRequestTabRefs = new StaffRequestTabRefs();
  obj: SearchRequest = null;
  query: SearchRequests = null;
  request: SearchRequestsRequest = null;
  patientName: string = "";
  isInvisibileNotification: boolean = true;
  staffAddRequestPopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public userDisposable: D3EDisposable;
  public constructor(props: StaffRequestTabProps) {
    super(props);

    this.initState();
  }
  public get user(): NOKUser {
    return this.props.user;
  }
  public get admin(): Administrator {
    return this.props.admin;
  }
  public get notification(): Notification {
    return this.props.notification;
  }
  public initState() {
    super.initState();

    this.runFetchDataQueryForUser();

    this.userDisposable = MessageDispatch.get().syncObject(
      this.props.user,
      UsageConstants.QUERY_GETNOKUSERBYID_STAFFREQUESTTAB_USER_FETCHDATA
    );

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("user", this.props.user);

    this.updateSyncProperty("admin", this.props.admin);

    this.on(
      [
        "request",
        "request.applyStatus",
        "request.ascending",
        "request.createdBy",
        "request.fromDate",
        "request.hospital",
        "request.name",
        "request.offset",
        "request.orderBy",
        "request.pageSize",
        "request.request",
        "request.status",
        "request.toDate",
      ],
      this.computeQuery
    );

    this.computeQuery();

    this.updateSyncProperty("notification", this.props.notification);

    this.on(
      [
        "isInvisibileNotification",
        "notification",
        "notification.title",
        "query",
        "query.count",
        "query.items",
        "query.items.createdBy",
        "query.items.createdBy.name",
        "query.items.failedMessage",
        "query.items.firstName",
        "query.items.notes",
        "query.items.requestDate",
        "query.items.searchId",
        "query.items.status",
        "request",
        "request.offset",
        "request.pageSize",
        "user",
        "user.hospital",
        "user.hospital.status",
        "user.userType",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: StaffRequestTabProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.runFetchDataQueryForUser();

      this.fire("user", this);
    }

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }

    if (prevProps.notification !== this.props.notification) {
      this.updateObservable(
        "notification",
        prevProps.notification,
        this.props.notification
      );

      this.fire("notification", this);
    }
  }
  public runFetchDataQueryForUser = (): void => {
    if (this.user == null) {
      return;
    }

    Query.get().getNOKUserById(
      UsageConstants.QUERY_GETNOKUSERBYID_STAFFREQUESTTAB_USER_FETCHDATA,
      this.user.id
    );
  };
  public setObj(val: SearchRequest): void {
    let isValChanged: boolean = this.obj !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("obj", this.obj, val);

    this.obj = val;

    this.fire("obj", this);
  }
  public setQuery(val: SearchRequests): void {
    let isValChanged: boolean = this.query !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("query", this.query, val);

    MessageDispatch.get().dispose(this.query);

    this.query = val;

    this.fire("query", this);
  }
  public computeQuery = async (): Promise<void> => {
    try {
      this.setQuery(
        await Query.get().getSearchRequests(
          UsageConstants.QUERY_GETSEARCHREQUESTS_STAFFREQUESTTAB_PROPERTIES_QUERY_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(" exception in computeQuery : " + exception.toString());

      this.setQuery(null);
    }
  };
  public setRequest(val: SearchRequestsRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setPatientName(val: string): void {
    let isValChanged: boolean = this.patientName !== val;

    if (!isValChanged) {
      return;
    }

    this.patientName = val;

    this.fire("patientName", this);
  }
  public setIsInvisibileNotification(val: boolean): void {
    let isValChanged: boolean = this.isInvisibileNotification !== val;

    if (!isValChanged) {
      return;
    }

    this.isInvisibileNotification = val;

    this.fire("isInvisibileNotification", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      children: [
        this.user !== null && this.user.userType === UserType.Staff
          ? ui.Row({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
              children: [
                TextView({
                  data: "Requests",
                  style: new ui.TextStyle({
                    fontSize: cStyle.tTextViewHeadlineThreeFontSizeOn,
                    fontWeight: cStyle.tTextViewHeadlineThreeFontWeightOn,
                  }),
                  className: "xc8f",
                  key: "0",
                }),
                this.user.hospital.status === HospitalStatus.Active
                  ? AddButtonWithState({
                      d3eState: this.d3eState,
                      _onAddButtonClicked: this.onAddButtonClicked,
                    })
                  : [],
              ],
              className: "x8b1 hc h",
            })
          : [],
        ui.Column({
          children: [
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              children: [
                ui.Container({
                  margin: ui.EdgeInsets.fromLTRB(
                    12.0,
                    0.0,
                    0.0,
                    10.0,
                    new Map()
                  ),
                  width: 350,
                  child: SearchComponent({
                    placeholder: "Search With Patient Name...",
                    onChanged: (value) => {
                      this.onChangedSearchComponentHandler(
                        value,
                        this.d3eState
                      );
                    },
                  }),
                  key: "0",
                  className: "x10ec hc",
                }),
              ],
              className: "xd41d hc h",
              key: "0",
            }),
            ui.Column({
              children: [
                ui.Column({
                  children: [
                    ui.Table({
                      defaultColumnWidth: new ui.FlexColumnWidth(1),
                      columnWidths: {
                        0: new ui.FlexColumnWidth(1),
                        1: new ui.FlexColumnWidth(1),
                        2: new ui.FlexColumnWidth(1),
                        3: new ui.FlexColumnWidth(1),
                        4: new ui.FlexColumnWidth(1),
                        5: new ui.FixedColumnWidth(150),
                      },
                      children: [
                        ui.TableRow({
                          children: [
                            ui.TableCell({
                              verticalAlignment:
                                ui.TableCellVerticalAlignment.middle,
                              child: TextView({
                                data: "Request ID",
                                style: new ui.TextStyle({
                                  fontFamily:
                                    cStyle.tTextViewTableHeaderFontFamilyOn,
                                  color: cStyle.tTextViewTableHeaderColorOn,
                                  fontSize:
                                    cStyle.tTextViewTableHeaderFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewTableHeaderFontWeightOn,
                                }),
                                className: "hc",
                              }),
                              className: "hc",
                              key: "0",
                            }),
                            ui.TableCell({
                              verticalAlignment:
                                ui.TableCellVerticalAlignment.middle,
                              child: TextView({
                                data: "Patient",
                                style: new ui.TextStyle({
                                  fontFamily:
                                    cStyle.tTextViewTableHeaderFontFamilyOn,
                                  color: cStyle.tTextViewTableHeaderColorOn,
                                  fontSize:
                                    cStyle.tTextViewTableHeaderFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewTableHeaderFontWeightOn,
                                }),
                                className: "hc",
                              }),
                              className: "hc",
                              key: "1",
                            }),
                            this.user !== null &&
                            this.user.userType === UserType.Admin
                              ? ui.TableCell({
                                  verticalAlignment:
                                    ui.TableCellVerticalAlignment.middle,
                                  child: TextView({
                                    data: "Staff",
                                    style: new ui.TextStyle({
                                      fontFamily:
                                        cStyle.tTextViewTableHeaderFontFamilyOn,
                                      color: cStyle.tTextViewTableHeaderColorOn,
                                      fontSize:
                                        cStyle.tTextViewTableHeaderFontSizeOn,
                                      fontWeight:
                                        cStyle.tTextViewTableHeaderFontWeightOn,
                                    }),
                                    className: "hc",
                                  }),
                                  className: "hc",
                                })
                              : [],
                            ui.TableCell({
                              child: TextView({
                                data: "Request Date",
                                style: new ui.TextStyle({
                                  fontFamily:
                                    cStyle.tTextViewTableHeaderFontFamilyOn,
                                  color: cStyle.tTextViewTableHeaderColorOn,
                                  fontSize:
                                    cStyle.tTextViewTableHeaderFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewTableHeaderFontWeightOn,
                                }),
                                className: "hc",
                              }),
                              className: "hc",
                              key: "3",
                            }),
                            ui.TableCell({
                              child: TextView({
                                data: "Note",
                                style: new ui.TextStyle({
                                  fontFamily:
                                    cStyle.tTextViewTableHeaderFontFamilyOn,
                                  color: cStyle.tTextViewTableHeaderColorOn,
                                  fontSize:
                                    cStyle.tTextViewTableHeaderFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewTableHeaderFontWeightOn,
                                }),
                                className: "hc",
                              }),
                              className: "hc",
                              key: "4",
                            }),
                            ui.TableCell({
                              child: TextView({
                                data: "Status",
                                style: new ui.TextStyle({
                                  fontFamily:
                                    cStyle.tTextViewTableHeaderFontFamilyOn,
                                  color: cStyle.tTextViewTableHeaderColorOn,
                                  fontSize:
                                    cStyle.tTextViewTableHeaderFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewTableHeaderFontWeightOn,
                                }),
                                className: "hc",
                              }),
                              className: "hc",
                              key: "5",
                            }),
                          ],
                          className: "xed1 hc",
                          key: "0",
                        }),
                        this.query !== null && this.query.items.isNotEmpty
                          ? (this.query?.items ?? []).expand((forItem) => [
                              ui.TableRow({
                                states: ui.joinStates(
                                  {
                                    "data-c0":
                                      this.notification !== null &&
                                      forItem.searchId ===
                                        this.notification.title &&
                                      this.isInvisibileNotification,
                                  },
                                  {}
                                ),
                                decoration:
                                  this.notification !== null &&
                                  forItem.searchId ===
                                    this.notification.title &&
                                  this.isInvisibileNotification
                                    ? new ui.BoxDecoration({
                                        color: new ui.Color(0xffe9ecf2),
                                      })
                                    : new ui.BoxDecoration({}),
                                children: [
                                  ui.TableCell({
                                    verticalAlignment:
                                      ui.TableCellVerticalAlignment.middle,
                                    child: TextView({
                                      data: forItem.searchId,
                                      softWrap:
                                        cStyle.tTextViewTableCellTextSoftWrapOn,
                                      style: new ui.TextStyle({
                                        fontFamily:
                                          cStyle.tTextViewTableCellTextFontFamilyOn,
                                        color:
                                          cStyle.tTextViewTableCellTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewTableCellTextFontSizeOn,
                                      }),
                                      className: "hc",
                                    }),
                                    className: "hc",
                                    key: "0",
                                  }),
                                  ui.TableCell({
                                    verticalAlignment:
                                      ui.TableCellVerticalAlignment.middle,
                                    child: TextView({
                                      data: forItem.firstName,
                                      softWrap:
                                        cStyle.tTextViewTableCellTextSoftWrapOn,
                                      style: new ui.TextStyle({
                                        fontFamily:
                                          cStyle.tTextViewTableCellTextFontFamilyOn,
                                        color:
                                          cStyle.tTextViewTableCellTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewTableCellTextFontSizeOn,
                                      }),
                                      className: "hc",
                                    }),
                                    className: "hc",
                                    key: "1",
                                  }),
                                  this.user !== null &&
                                  this.user.userType === UserType.Admin
                                    ? ui.TableCell({
                                        verticalAlignment:
                                          ui.TableCellVerticalAlignment.middle,
                                        child: TextView({
                                          data: forItem.createdBy.name,
                                          softWrap:
                                            cStyle.tTextViewTableCellTextSoftWrapOn,
                                          style: new ui.TextStyle({
                                            fontFamily:
                                              cStyle.tTextViewTableCellTextFontFamilyOn,
                                            color:
                                              cStyle.tTextViewTableCellTextColorOn,
                                            fontSize:
                                              cStyle.tTextViewTableCellTextFontSizeOn,
                                          }),
                                          className: "hc",
                                        }),
                                        className: "hc",
                                      })
                                    : [],
                                  ui.TableCell({
                                    verticalAlignment:
                                      ui.TableCellVerticalAlignment.middle,
                                    child: TextView({
                                      data: DateUtils.format(
                                        forItem.requestDate
                                      ),
                                      softWrap:
                                        cStyle.tTextViewTableCellTextSoftWrapOn,
                                      style: new ui.TextStyle({
                                        fontFamily:
                                          cStyle.tTextViewTableCellTextFontFamilyOn,
                                        color:
                                          cStyle.tTextViewTableCellTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewTableCellTextFontSizeOn,
                                      }),
                                      className: "hc",
                                    }),
                                    className: "hc",
                                    key: "3",
                                  }),
                                  ui.TableCell({
                                    verticalAlignment:
                                      ui.TableCellVerticalAlignment.middle,
                                    child: TextView({
                                      data: forItem.notes,
                                      softWrap:
                                        cStyle.tTextViewTableCellTextSoftWrapOn,
                                      style: new ui.TextStyle({
                                        fontFamily:
                                          cStyle.tTextViewTableCellTextFontFamilyOn,
                                        color:
                                          cStyle.tTextViewTableCellTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewTableCellTextFontSizeOn,
                                      }),
                                      className: "hc",
                                    }),
                                    className: "hc",
                                    key: "4",
                                  }),
                                  ui.TableCell({
                                    verticalAlignment:
                                      ui.TableCellVerticalAlignment.middle,
                                    child: ui.Row({
                                      children: [
                                        TextView({
                                          data: forItem.status.name,
                                          softWrap:
                                            cStyle.tTextViewTableCellTextSoftWrapOn,
                                          states: ui.joinStates(
                                            {
                                              "data-c0":
                                                forItem.status ===
                                                  RequestStatus.Processing ||
                                                forItem.status ===
                                                  RequestStatus.Failed,
                                              "data-c1":
                                                forItem.status ===
                                                RequestStatus.Resolved,
                                            },
                                            {}
                                          ),
                                          style: new ui.TextStyle({
                                            fontFamily:
                                              cStyle.tTextViewTableCellTextFontFamilyOn,
                                            color:
                                              forItem.status ===
                                                RequestStatus.Processing ||
                                              forItem.status ===
                                                RequestStatus.Failed
                                                ? cStyle.c25
                                                : forItem.status ===
                                                  RequestStatus.Resolved
                                                ? cStyle.c24
                                                : cStyle.tTextViewTableCellTextColorOn,
                                            fontSize:
                                              cStyle.tTextViewTableCellTextFontSizeOn,
                                          }),
                                          className: "x4a8",
                                          key: "0",
                                        }),
                                        forItem.status === RequestStatus.Failed
                                          ? ToolTipInfoMessage({
                                              message: forItem.failedMessage,
                                            })
                                          : ui.Container({}),
                                      ],
                                      className: "x7a4a hc h",
                                    }),
                                    className: "xfc8 hc h",
                                    key: "5",
                                  }),
                                ],
                                onTap: (e) => {
                                  e.stopPropagation();

                                  this.onEditHandler(forItem, this.d3eState);
                                },
                                className: "x8df hc h",
                                key: forItem?.ident,
                              }),
                            ])
                          : [],
                      ],
                      className: "xe30 hc h",
                      key: "0",
                    }),
                    this.query !== null && this.query.items.isEmpty
                      ? ui.Column({
                          mainAxisAlignment: ui.MainAxisAlignment.center,
                          children: [
                            ui.AssetImage({
                              path: "images/emptyList.png",
                              width: 300,
                              height: 300,
                              className: "x211 hc vc",
                              key: "0",
                            }),
                            TextView({
                              data: "No Records",
                              style: new ui.TextStyle({
                                fontWeight: ui.FontWeight.bold,
                                fontSize: 16,
                              }),
                              className: "xb5b hc",
                              key: "1",
                            }),
                          ],
                          className: "x248e hc",
                        })
                      : [],
                    ui.Row({
                      mainAxisAlignment: ui.MainAxisAlignment.end,
                      children: [
                        PaginationView({
                          pageSize: this.request.pageSize,
                          total: this.query !== null ? this.query.count : 0,
                          offset: this.request.offset,
                          onChange: (pageSize, offset) => {
                            this.onPaginationViewChanged(
                              pageSize,
                              offset,
                              this.d3eState
                            );
                          },
                          states: ui.joinStates(
                            {
                              "data-visibility":
                                this.query !== null &&
                                this.query.items.isNotEmpty,
                            },
                            {}
                          ),
                          key: "0",
                        }),
                      ],
                      className: "x94c hc h",
                      key: "2",
                    }),
                  ],
                  className: "x08a hc h",
                  key: "0",
                }),
              ],
              className: "x469 hc h",
              key: "1",
            }),
          ],
          className: "x017 hc h",
          key: "1",
        }),
      ],
      className: ui.join(this.props.className, "StaffRequestTab x4e8 hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    new Timer(new Duration({ seconds: 5 }), () => {
      this.setIsInvisibileNotification(false);
    });

    if (this.user !== null && this.user.userType === UserType.Staff) {
      this.setRequest(
        new SearchRequestsRequest({
          pageSize: 50,
          offset: 0,
          orderBy: "sortOrder",
          ascending: false,
          applyStatus: false,
          status: RequestStatus.Processing,
          hospital: this.user.hospital,
          createdBy: this.user,
        })
      );
    } else if (this.user !== null && this.user.userType === UserType.Admin) {
      this.setRequest(
        new SearchRequestsRequest({
          pageSize: 50,
          offset: 0,
          orderBy: "sortOrder",
          ascending: false,
          applyStatus: false,
          status: RequestStatus.Processing,
          hospital: this.user.hospital,
        })
      );
    }
  };
  public onPaginationViewChanged = (
    pageSize: number,
    offset: number,
    d3eState: StaffRequestTabRefs
  ): void => {
    this.request.setPageSize(pageSize);

    this.request.setOffset(offset);

    this.setRequest(this.request.deepClone());
  };
  public onEditHandler = (
    forItem: SearchRequest,
    d3eState: StaffRequestTabRefs
  ): void => {
    this.navigator.pushSearchRequestResponseView({
      obj: forItem,
      target: "main",
      replace: false,
    });
  };
  public onAddButtonClicked = (d3eState: StaffRequestTabRefs): void => {
    this.setObj(
      new SearchRequest({
        hospital: this.user.hospital,
        searchId: HospitalUtils.generateSearchId(this.user.hospital),
      })
    );

    this.showStaffAddRequest();
  };
  public onChangedSearchComponentHandler = (
    value: string,
    d3eState: StaffRequestTabRefs
  ): void => {
    this.setPatientName(value);

    this.request.setName(value);

    this.setRequest(this.request.deepClone());
  };
  public dispose(): void {
    this.userDisposable?.dispose();

    MessageDispatch.get().dispose(this.query);

    this.staffAddRequestPopup?.dispose();

    super.dispose();
  }
  public showStaffAddRequest(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.staffAddRequestPopup?.dispose();

    /*
TODO ThemeWrapper.of(this.context);
*/

    let cStyle = StyleThemeData.current;

    this.staffAddRequestPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Center,
      child: ui.Container({
        width: 860,
        child: StaffAddOrEditRequestView({
          obj: this.obj,
          user: this.user,
          admin: this.admin,
        }),
        className: "xb42a hc vc",
      }),
    });

    this.staffAddRequestPopup.showPopup(this.context);
  }
  public hideStaffAddRequest(): void {
    this.staffAddRequestPopup?.dispose();
  }
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get addButton() {
    return this.d3eState.addButton;
  }
}
export default function StaffRequestTab(props: StaffRequestTabProps) {
  return React.createElement(_StaffRequestTabState, {
    ..._StaffRequestTabState.defaultProps,
    ...props,
  });
}
